import { useMutation } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import useLead from './useLead';
import { verifyOffersSynchronizationStatus } from '../utils/verifyOffersSynchronizationStatus';
import { APICommunicator } from '~/api/APICommunicator';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import { queryKeys } from '~/utils/queryKeys';

export const useOffersSynchronizationMutation = (loanRequestId: string) => {
    const { t } = useTranslation();
    const lead = useLead(loanRequestId);
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        mutationFn: async () => {
            if (!lead?.data?.europace?.id) throw new Error();

            return APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.requestEuropaceOffersSynchronization,
                payload: {
                    platform: lead.data.europace.platform,
                },
            });
        },
        onError: () => {
            toast.error(t('OFFER_MANAGEMENT.OFFERS_SYNCHRONIZATION.TOASTS.COMMAND_FAILED'));
        },
        onSuccess: ({ aggregateId, id: correlationId }) => {
            invalidateOnChainComplete({
                correlationId,
                queryKeys: [queryKeys.getAcceptedOffers(aggregateId), queryKeys.getAcceptedOfferCounts(aggregateId)],
                onComplete: () => {
                    verifyOffersSynchronizationStatus({
                        loanRequestId: aggregateId,
                        t,
                    });
                },
            });
        },
    });
};
